import { useCallback, useMemo } from "react";
import api from "../../services/api";

const useCompanySettings = () => {
  const getAll = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings",
      method: "GET",
    });

    return data;
  }, []);

  const get = useCallback(async (params) => {
    const { data } = await api.request({
      url: "/companySettingOne",
      method: "GET",
      params,
    });
    return data;
  }, []);

  const update = useCallback(async (payload) => {
    const { data } = await api.request({
      url: "/companySettings",
      method: "PUT",
      data: payload,
    });
    return data;
  }, []);

  const reindexElastic = useCallback(async (payload = {}) => {
    const { data } = await api.request({
      url: "/companySettings/haba/elasticsearch/reindex",
      method: "POST",
      data: payload
    });
    return data;
  }, []);

  const startReindexElasticJob = useCallback(async (payload = {}) => {
    const { data } = await api.request({
      url: "/companySettings/haba/elasticsearch/reindex/start",
      method: "POST",
      data: payload
    });
    return data;
  }, []);

  const getReindexElasticJobStatus = useCallback(async (jobId) => {
    const { data } = await api.request({
      url: `/companySettings/haba/elasticsearch/reindex/status/${jobId}`,
      method: "GET"
    });
    return data;
  }, []);

  const runElasticMaintenance = useCallback(async (payload) => {
    const { data } = await api.request({
      url: "/companySettings/haba/elasticsearch/maintenance",
      method: "POST",
      data: payload
    });

    return data;
  }, []);

  const getElasticMaintenanceSchedule = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/haba/elasticsearch/maintenance/schedule",
      method: "GET"
    });

    return data;
  }, []);

  const updateElasticMaintenanceSchedule = useCallback(async (payload) => {
    const { data } = await api.request({
      url: "/companySettings/haba/elasticsearch/maintenance/schedule",
      method: "PUT",
      data: payload
    });

    return data;
  }, []);

  const createElasticSnapshotBackup = useCallback(async (payload = {}) => {
    const { data } = await api.request({
      url: "/companySettings/haba/elasticsearch/backup/snapshot",
      method: "POST",
      data: payload
    });

    return data;
  }, []);

  const exportElasticSnapshotsBackup = useCallback(async (params = {}) => {
    const { data, headers } = await api.request({
      url: "/companySettings/haba/elasticsearch/backup/export",
      method: "GET",
      params,
      responseType: "blob"
    });

    return { data, headers };
  }, []);

  const exportDatabaseBackup = useCallback(async () => {
    const { data, headers } = await api.request({
      url: "/companySettings/database/backup/export",
      method: "GET",
      responseType: "blob"
    });

    return { data, headers };
  }, []);

  const importDatabaseBackup = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await api.request({
      url: "/companySettings/database/backup/import",
      method: "POST",
      data: formData
    });

    return data;
  }, []);

  const runDatabaseMaintenance = useCallback(async (payload) => {
    const { data } = await api.request({
      url: "/companySettings/database/maintenance",
      method: "POST",
      data: payload
    });

    return data;
  }, []);

  const getDatabaseMaintenanceSchedule = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/database/maintenance/schedule",
      method: "GET"
    });

    return data;
  }, []);

  const updateDatabaseMaintenanceSchedule = useCallback(async (payload) => {
    const { data } = await api.request({
      url: "/companySettings/database/maintenance/schedule",
      method: "PUT",
      data: payload
    });

    return data;
  }, []);

  const getGoogleContactsStatus = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/google-contacts/status",
      method: "GET"
    });

    return data;
  }, []);

  const saveGoogleContactsCredentials = useCallback(async (credentials) => {
    const { data } = await api.request({
      url: "/companySettings/google-contacts/credentials",
      method: "POST",
      data: { credentials }
    });

    return data;
  }, []);

  const toggleGoogleContacts = useCallback(async (enabled) => {
    const { data } = await api.request({
      url: "/companySettings/google-contacts/toggle",
      method: "POST",
      data: { enabled }
    });

    return data;
  }, []);

  const getGoogleContactsAuthUrl = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/google-contacts/auth-url",
      method: "GET"
    });

    return data;
  }, []);

  const getGoogleCalendarStatus = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/google-calendar/status",
      method: "GET"
    });

    return data;
  }, []);

  const getGoogleCalendarAuthUrl = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/google-calendar/auth-url",
      method: "GET"
    });

    return data;
  }, []);

  const getGoogleCalendarCalendars = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/google-calendar/calendars",
      method: "GET"
    });

    return data;
  }, []);

  const saveGoogleCalendarConfig = useCallback(async (payload) => {
    const { data } = await api.request({
      url: "/companySettings/google-calendar/config",
      method: "POST",
      data: payload
    });

    return data;
  }, []);

  const syncGoogleCalendarModule = useCallback(async (moduleKey) => {
    const { data } = await api.request({
      url: `/companySettings/google-calendar/sync/${moduleKey}`,
      method: "POST"
    });

    return data;
  }, []);

  const startGoogleCalendarSyncJob = useCallback(async (moduleKey) => {
    const { data } = await api.request({
      url: `/companySettings/google-calendar/sync/${moduleKey}/start`,
      method: "POST"
    });

    return data;
  }, []);

  const getGoogleCalendarSyncJobStatus = useCallback(async (jobId) => {
    const { data } = await api.request({
      url: `/companySettings/google-calendar/sync/status/${jobId}`,
      method: "GET"
    });

    return data;
  }, []);

  const exportRedisBackup = useCallback(async () => {
    const { data, headers } = await api.request({
      url: "/companySettings/redis/backup/export",
      method: "GET",
      responseType: "blob"
    });

    return { data, headers };
  }, []);

  const importRedisBackup = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await api.request({
      url: "/companySettings/redis/backup/import",
      method: "POST",
      data: formData
    });

    return data;
  }, []);

  const runRedisMaintenance = useCallback(async (payload) => {
    const { data } = await api.request({
      url: "/companySettings/redis/maintenance",
      method: "POST",
      data: payload
    });

    return data;
  }, []);

  const getRedisMaintenanceSchedule = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/redis/maintenance/schedule",
      method: "GET"
    });

    return data;
  }, []);

  const updateRedisMaintenanceSchedule = useCallback(async (payload) => {
    const { data } = await api.request({
      url: "/companySettings/redis/maintenance/schedule",
      method: "PUT",
      data: payload
    });

    return data;
  }, []);

  const getGeneralMaintenanceSchedule = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/general-maintenance/schedule",
      method: "GET"
    });

    return data;
  }, []);

  const updateGeneralMaintenanceSchedule = useCallback(async (payload) => {
    const { data } = await api.request({
      url: "/companySettings/general-maintenance/schedule",
      method: "PUT",
      data: payload
    });

    return data;
  }, []);

  const runManualGarbageCollection = useCallback(async () => {
    const { data } = await api.request({
      url: "/companySettings/general-maintenance/run-gc",
      method: "POST"
    });

    return data;
  }, []);

  return useMemo(
    () => ({
      getAll,
      get,
      update,
      reindexElastic,
      startReindexElasticJob,
      getReindexElasticJobStatus,
      runElasticMaintenance,
      getElasticMaintenanceSchedule,
      updateElasticMaintenanceSchedule,
      createElasticSnapshotBackup,
      exportElasticSnapshotsBackup,
      exportDatabaseBackup,
      importDatabaseBackup,
      runDatabaseMaintenance,
      getDatabaseMaintenanceSchedule,
      updateDatabaseMaintenanceSchedule,
      getGoogleContactsStatus,
      saveGoogleContactsCredentials,
      toggleGoogleContacts,
      getGoogleContactsAuthUrl,
      getGoogleCalendarStatus,
      getGoogleCalendarAuthUrl,
      getGoogleCalendarCalendars,
      saveGoogleCalendarConfig,
      syncGoogleCalendarModule,
      startGoogleCalendarSyncJob,
      getGoogleCalendarSyncJobStatus,
      exportRedisBackup,
      importRedisBackup,
      runRedisMaintenance,
      getRedisMaintenanceSchedule,
      updateRedisMaintenanceSchedule,
      getGeneralMaintenanceSchedule,
      updateGeneralMaintenanceSchedule,
      runManualGarbageCollection
    }),
    [
      getAll,
      get,
      update,
      reindexElastic,
      startReindexElasticJob,
      getReindexElasticJobStatus,
      runElasticMaintenance,
      getElasticMaintenanceSchedule,
      updateElasticMaintenanceSchedule,
      createElasticSnapshotBackup,
      exportElasticSnapshotsBackup,
      exportDatabaseBackup,
      importDatabaseBackup,
      runDatabaseMaintenance,
      getDatabaseMaintenanceSchedule,
      updateDatabaseMaintenanceSchedule,
      getGoogleContactsStatus,
      saveGoogleContactsCredentials,
      toggleGoogleContacts,
      getGoogleContactsAuthUrl,
      getGoogleCalendarStatus,
      getGoogleCalendarAuthUrl,
      getGoogleCalendarCalendars,
      saveGoogleCalendarConfig,
      syncGoogleCalendarModule,
      startGoogleCalendarSyncJob,
      getGoogleCalendarSyncJobStatus,
      exportRedisBackup,
      importRedisBackup,
      runRedisMaintenance,
      getRedisMaintenanceSchedule,
      updateRedisMaintenanceSchedule,
      getGeneralMaintenanceSchedule,
      updateGeneralMaintenanceSchedule,
      runManualGarbageCollection
    ]
  );
};

export default useCompanySettings;
